import { useState, useCallback } from 'react';
import { 
  collection, 
  doc, 
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './useAuth';

interface ClientInsight {
  id: string;
  clientId: string;
  userId: string;
  type: 'key_message' | 'tone' | 'preference' | 'language' | 'audience' | 'writing_style' | 'press_release_analysis' | 'other';
  content: string;
  source: 'conversation' | 'manual' | 'press_release_analysis' | 'ai_agent';
  sourceId?: string;
  sourceUrl?: string;
  confidence: number; // 0-1 score of how confident the AI is about this insight
  createdAt: Date | string | null | undefined;
  updatedAt: Date | string | null | undefined;
  verified: boolean; // Whether a human has verified this insight
}

/**
 * Custom hook for managing AI-generated client insights
 */
export const useClientInsights = () => {
  const { currentUser } = useAuth();
  const [insights, setInsights] = useState<ClientInsight[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch insights for a specific client
  const fetchInsights = useCallback(async (clientId: string) => {
    if (!currentUser?.uid || !clientId) return [];
    
    setLoading(true);
    try {
      const insightsQuery = query(
        collection(db, 'clientInsights'),
        where('userId', '==', currentUser.uid),
      );
      
      const querySnapshot = await getDocs(insightsQuery);
      const insightsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Convert Firestore timestamps to JavaScript dates
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        } as ClientInsight;
      }).filter((insight) => insight.clientId === clientId)
        .sort((a, b) => {
          const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
          const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
          return bTime - aTime;
        })
        .slice(0, 50);
      
      setInsights(insightsData);
      setError(null);
      return insightsData;
    } catch (err) {
      console.error('Error fetching client insights:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      return [];
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Add a new client insight
  const addInsight = useCallback(async (
    clientId: string, 
    insightData: Omit<ClientInsight, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    if (!currentUser?.uid || !clientId) return null;
    
    try {
      const insightRef = doc(collection(db, 'clientInsights'));
      const timestamp = serverTimestamp();
      
      const newInsight = {
        ...insightData,
        id: insightRef.id,
        clientId,
        userId: currentUser.uid,
        createdAt: timestamp,
        updatedAt: timestamp
      };
      
      await setDoc(insightRef, newInsight);
      
      // Add to local state with proper date objects
      setInsights(prev => [{
        ...newInsight,
        createdAt: new Date(),
        updatedAt: new Date()
      } as ClientInsight, ...prev]);
      
      return newInsight.id;
    } catch (err) {
      console.error('Error adding client insight:', err);
      return null;
    }
  }, [currentUser?.uid]);

  // Verify an insight (mark as human-verified)
  const verifyInsight = useCallback(async (insightId: string, isVerified: boolean) => {
    if (!currentUser?.uid) return false;
    
    try {
      const insightRef = doc(db, 'clientInsights', insightId);
      
      await updateDoc(insightRef, {
        verified: isVerified,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setInsights(prev =>
        prev.map(insight =>
          insight.id === insightId
            ? { ...insight, verified: isVerified, updatedAt: new Date() }
            : insight
        )
      );
      
      return true;
    } catch (err) {
      console.error('Error verifying client insight:', err);
      return false;
    }
  }, [currentUser?.uid]);

  // Delete an insight
  const deleteInsight = useCallback(async (insightId: string) => {
    if (!currentUser?.uid) return false;
    
    try {
      const insightRef = doc(db, 'clientInsights', insightId);
      
      await updateDoc(insightRef, {
        deleted: true,
        updatedAt: serverTimestamp()
      });
      
      // Remove from local state
      setInsights(prev => prev.filter(insight => insight.id !== insightId));
      
      return true;
    } catch (err) {
      console.error('Error deleting client insight:', err);
      return false;
    }
  }, [currentUser?.uid]);

  return {
    insights,
    loading,
    error,
    fetchInsights,
    addInsight,
    verifyInsight,
    deleteInsight
  };
};
